import { Link } from 'react-router-dom'
import { ArrowRight, MessageSquare, Users, Sparkles } from 'lucide-react'
import FloatingBackground from '../components/FloatingBackground'
import Seo from '../components/Seo'
import { FadeIn } from '../components/Motion'

const modelComponents = [
  {
    icon: Users,
    label: 'Per campaign',
    body: 'A flat fee to launch a campaign — questions, audience, analytics, and the campaign workspace.',
  },
  {
    icon: MessageSquare,
    label: 'Per interview',
    body: 'Pay for the AI-moderated conversations you run. Each interview includes adaptive follow-ups, a full transcript, and per-respondent analysis.',
  },
  {
    icon: Sparkles,
    label: 'Chat with your data',
    tag: 'Optional',
    body: 'Add the analysis engine to query your results in natural language, surface themes across campaigns, and dig into transcripts without leaving the workspace.',
  },
]

export default function Pricing() {
  return (
    <div>
      <Seo
        title="Pricing"
        description="How PrismScope pricing works: a per-campaign fee, per-interview usage, and an optional chat-with-your-data analysis engine. Contact us for a quote."
        path="/pricing"
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <FloatingBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Pricing
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.95]">
              Simple, transparent{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                  pricing
                </span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#8b5cf6]/25 -z-0 -skew-y-1"
                />
              </span>
              <span className="text-[#00008B]">.</span>
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-2xl mx-auto leading-relaxed">
              Pay for the campaigns you run and the interviews you gather — nothing else.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── Model explainer ──────────────────────────────────────────────── */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              How pricing works
            </h2>
            <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
              Three components. No platform fee, no seat licenses, no minimum commitment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {modelComponents.map(({ icon: Icon, label, tag, body }) => (
              <div
                key={label}
                className="rounded-xl border border-intelligence-200 bg-white p-6 flex flex-col"
              >
                <div className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-intelligence-900">{label}</h3>
                  {tag && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                      {tag}
                    </span>
                  )}
                </div>
                <p className="text-intelligence-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          {/* Coming-soon note */}
          <div className="mt-10 max-w-3xl mx-auto bg-white border border-intelligence-200 rounded-xl px-6 py-5 text-center">
            <p className="text-sm font-semibold text-intelligence-900 mb-1">
              Detailed pricing is being finalized.
            </p>
            <p className="text-sm text-intelligence-600">
              We&apos;re updating our published rates. In the meantime, reach out and we&apos;ll put together a quote based on the campaigns and interview volume you need.
            </p>
          </div>
        </div>
      </section>

      {/* ── Chat with your data feature callout ──────────────────────────── */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-primary-600 font-medium text-sm mb-2 uppercase tracking-wide">
                Optional add-on
              </p>
              <h2 className="text-3xl font-bold text-intelligence-900 mb-4">
                Chat with your data.
              </h2>
              <p className="text-intelligence-600 mb-6">
                Turn every campaign into a conversation. The analysis engine lets you ask questions in plain language — pull quotes, compare cohorts, chase a theme across campaigns — without exporting, filtering, or writing a single query.
              </p>
              <ul className="space-y-3 mb-8 text-sm text-intelligence-700">
                {[
                  'Natural-language questions over transcripts, themes, and sentiment',
                  'Cite the exact respondent and moment behind any answer',
                  'Compare across campaigns, cohorts, or time windows',
                  'Ships as an optional add-on — enable it when you need it',
                ].map((point) => (
                  <li key={point} className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 mr-3 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                Talk to us about the analysis engine <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-intelligence-50 rounded-xl border border-intelligence-200 p-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-intelligence-500 mb-4">
                Example
              </p>
              <div className="space-y-3">
                <div className="bg-white rounded-lg border border-intelligence-200 p-4">
                  <p className="text-xs font-medium text-intelligence-500 mb-1">You</p>
                  <p className="text-sm text-intelligence-800">
                    What did enterprise customers say about onboarding in the last two campaigns?
                  </p>
                </div>
                <div className="bg-primary-50 rounded-lg border border-primary-100 p-4">
                  <p className="text-xs font-medium text-primary-700 mb-1">PrismScope</p>
                  <p className="text-sm text-intelligence-800">
                    Across 34 enterprise respondents, three themes dominated: SSO setup friction, missing sandbox environments, and slow first-value. Onboarding NPS held at +12, but drops to &minus;4 among admins who hit SSO issues. Want the quotes?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">
              Your move
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tell us what you want to learn.
            </h2>
            <p className="text-lg text-intelligence-400 mb-10">
              Share the campaigns and interview volume you have in mind — we&apos;ll come back with a quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="relative inline-block group">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 rounded-lg bg-[#8b5cf6] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2"
                />
                <Link
                  to="/contact"
                  className="relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-intelligence-900 bg-white border-2 border-white rounded-lg"
                >
                  Contact us for pricing
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
